import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, glyphs, useMediaQuery } from "../../../Styles";
import { ESF_STATIONLIST, MainContentPaddingTypes, NewButtonList, NewModal, NewSelect, SystemControlsInner, ActiveStationsList, NewButton } from "../..";
import { useSectionOptions } from "../../_Components";
import { dispatchErrorWithTimeout, useEsfContext } from "../_EsfWalkThrough/EsfContext";
import { ListStyle_Types, MenuFloats } from "../../../Definitions";
import { selectStationByLocationId } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationSlice";
import { selectLocationsBySection } from "../../../Services/Agrisys_GqlDataCaller/SectionsCalls/locationSlice";
import { selectSectionsBySystem } from "../../../Services/Agrisys_GqlDataCaller/SectionsCalls/sectionsSlice";
import { useAppSelector } from "../../../Store";
import { selectLocationOptionsBySystem } from "../../../Store/OptionsHandlers/optionsCombinedReducer";
import { isEqual } from "lodash";
import { useGateControl, useStationAutoTune } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/gateControlSlice";

interface AutoTuneEsfWfLocation_Props {
  onClose: () => void;
  focus_view?: boolean;
}

export default function AutoTuneEsfWfLocationModal({ onClose, focus_view }: AutoTuneEsfWfLocation_Props) {
  const { t } = useTranslation();
  const { state: {system, selectedLocation, selectedStations, error}, dispatch } = useEsfContext();
  const mobile = useMediaQuery(`(max-width: 720px)`) || focus_view === true;
  const [loading, setLoading] = useState<boolean>(false);
  
  const sections = useAppSelector((state) => selectSectionsBySystem(state, system.system_id ?? -1), isEqual);
  const locations = useAppSelector((state) => selectLocationsBySection(state, sections.map(sec => sec.section_id)), isEqual);
  const stations = useAppSelector((state) => selectStationByLocationId(state, selectedLocation?.location_id ?? -1), isEqual);

  const { autoTune } = useStationAutoTune()
  
  useSectionOptions();
  const locationOptions = useAppSelector((state) => selectLocationOptionsBySystem(state, [system.system_id]), isEqual);
  
  useEffect(() => {
    if (!locations || locations.length === 0) return;
  
    const first = locations[0];
  
    if (!first?.stations) return;
  
    dispatch({
      selectedLocation: first,
      selectedSection: sections.find(s => s.section_id === first.sectionId),
      selectedStations: stations.filter(s => first.stations.includes(s.station_id))
    });
  }, [dispatch, sections]);

  function resetResultState() {
    // abortPreviousOperation();
    setLoading(false);
    dispatch({ locationResults: [], selectedStations: [] })
  }

  // Abort prev operation
  const abortControllerRef = useRef<AbortController | null>(null);
  function abortPreviousOperation() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }

  const doAutoTune = async () => {
    if (!selectedStations?.length) return;
    abortPreviousOperation();
  
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);

    const results = await autoTune( selectedStations.map((station) => ({ stationId: station.station_id, value: 2 })));
    
    if (!abortControllerRef.current) return;
    
    const autoTuneResults = results?.station.autoTune ?? [];
    const newLocationResults: { [key: number]: boolean } = {};
    let errorOccured = false;
    
    for (const result of autoTuneResults) {
      newLocationResults[result.id] = result.success;
    
      if (!result.success && !errorOccured) {
        errorOccured = true;
    
        dispatchErrorWithTimeout(dispatch, {
          Elementid: 1,
          errorText: t("systemControls.general.autoTuneFailed", { defaultValue: "Autotune failed" })
        });
      }
    }
    
    if (!errorOccured) {
      dispatchErrorWithTimeout(dispatch, {
        Elementid: 2,
        errorText: t("success", { defaultValue: "Success" })
      });
    }
    
    setLoading(false);
    dispatch({ locationResults: newLocationResults });
    abortControllerRef.current = null;
  }

  if (!locations) return null;
  const currentIndex = locations.findIndex((l) => l.location_id === selectedLocation?.location_id);

  if (!selectedLocation) return;

  const allSelected = stations.every(stationId => selectedStations?.includes(stationId));

  return (
    <NewModal
      header={t("systemControls.general.systemControls", { defaultValue: "System Controls" })}
      subHeader={selectedLocation?.name}
      overrides={{
        backgroundColor: colors.greyLightTertiary,
        mainContentPadding: MainContentPaddingTypes.primary,
        headerContentHeight: focus_view ? "60px" : undefined,
        mainContentScroll: mobile ? true : false
      }}
      responsiveOverrides={{
        focus_view: focus_view,
        mobile: mobile
      }}
      predefinedButtons={{
        onClose: () => { dispatch({selectedLocation: locations[0]});  resetResultState(); /* setStaticFeedAmount(0); */ onClose() },
        onNext: () => { dispatch({selectedLocation: locations[(currentIndex + 1) % locations.length]}); resetResultState() },
        onPrev: () => { dispatch({selectedLocation: locations[(currentIndex - 1 + locations.length) % locations.length]}); resetResultState() },
      }}
      headerContent={selectedLocation ? (
        <NewSelect
          options={locationOptions ?? []}
          key={selectedLocation.location_id}
          onChange={(i) => { /*resetResultState();*/ dispatch({selectedLocation: locations.find(location => location.location_id === i?.id)})}}
          valueOverrides={{
            defaultValue: locationOptions?.find(option => option.id === selectedLocation.location_id)
          }}
          fieldOverrides={{
            fieldWidth: mobile ? "120px" : "160px",
            focus_view: focus_view,
            menuWidth: mobile ? "70vw" : undefined,
            menuFloat: MenuFloats.center
          }}
        />
      ) : (<></>)}
      mainContent={
        <SystemControlsInner
          activeLocationsListHeader={(t("systemControls.general.controls", { defaultValue: "Controls" }))}
          focus_view={focus_view}
          locationVisual={ selectedLocation &&
            <ESF_STATIONLIST
              key={selectedLocation.location_id}
              focus_view={focus_view}
              location={selectedLocation}
            />
          }
          activeLocationsList={
            <ActiveStationsList
              focus_view={focus_view}
            />
          }
          error={{
            errorText: error.errorText,
            elementId: error.Elementid
          }}
          selectLocation={{
            selectedLocations: selectedStations ?? [],
            isAllSelected: allSelected,
            handleSelectAll: () => {
              if (allSelected) { dispatch({ selectedStations: selectedStations?.filter(stationId => !stations.includes(stationId)) });
              } else {
                const newSelection = Array.from(new Set([...selectedStations ?? [], ...stations]));
                dispatch({ selectedStations: newSelection });
              }
            }
          }}
          selectContent={<></>}
          button={{
            content: t("systemControls.general.autotuneSelected", { defaultValue: "autotune selected" }),
            startEnhancer: "Calibrate",
            onClick: () => doAutoTune(),
            disabled: loading || !selectedStations || selectedStations?.length === 0,
          }}
        />
      }
    />
  )
}